import { OlympusClient } from '@/OlympusClient'

/**
 * Uploads a PDF file to Owner's CDN and returns the resulting public URL.
 *
 * The file is stored with `Content-Type: application/pdf` (not
 * `application/octet-stream`). Owner's CDN serves objects with
 * `x-content-type-options: nosniff`, so a mistyped object can never be
 * re-interpreted as a PDF by the browser — the content type set here is the
 * content type the PDF viewers receive.
 *
 * @param file The PDF file to upload
 * @returns The public CDN URL of the uploaded PDF
 */
export const uploadPdf = async (file: File): Promise<string> => {
  const { resultUrl, uploadUrl } = await OlympusClient.requestFileUploadKey({
    query: {
      contentType: 'application/pdf',
      group: 'document',
      suffix: '.pdf',
    },
  })

  // `fetch` only rejects on network failure, not on a 4xx/5xx from S3 (e.g. an
  // expired presigned URL). Without this guard a failed PUT would still return
  // `resultUrl`, and the editor would persist a CDN link pointing at content
  // that was never written.
  const response = await fetch(uploadUrl, {
    body: file,
    headers: {
      'Content-Type': 'application/pdf',
    },
    method: 'PUT',
  })

  if (!response.ok) {
    throw new Error(
      `Failed to upload PDF to CDN: ${response.status} ${response.statusText}`
    )
  }

  return resultUrl
}
